// Shared across every proposal: every interface string. Tokens like {count} are filled at render time.
// Edit here once; every client's page picks it up.

export const copy = {
  pageTitle: "A plan for your home | Adam Home Team",
  skipLink: "Skip to the plan",
  draftLabel: "Draft",
  draftHint: "Dashed underlines mark values still being verified.",
  placeholderLabel: "Image to come",
  units: {
    day: "{n} day",
    days: "{n} days",
    sqft: "{n} sq ft",
    perSqft: "/sq ft",
    acre: "acre",
    homes: "{n} homes",
    home: "{n} home",
    none: "Not reported",
  },
  nav: {
    homeLabel: "Back to the top",
    bookCall: "Book a call",
    menuOpen: "Open menu",
    menuClose: "Close menu",
    menuTitle: "Sections",
    links: [
      { id: "your-home", label: "Your home" },
      { id: "nearby", label: "Nearby sales" },
      { id: "market", label: "Market" },
      { id: "pricing", label: "Pricing" },
      { id: "launch", label: "Launch" },
      { id: "marketing", label: "Marketing" },
      { id: "proceeds", label: "Proceeds" },
      { id: "why-adam", label: "Why Adam" },
    ],
  },
  sectionNav: {
    top: null,
    "your-home": "your-home",
    nearby: "nearby",
    market: "market",
    pricing: "pricing",
    "pricing-strategy": "pricing",
    launch: "launch",
    "getting-ready": "launch",
    marketing: "marketing",
    communication: "marketing",
    "marketing-plan": "marketing",
    offers: "proceeds",
    proceeds: "proceeds",
    "why-adam": "why-adam",
    "easy-exit": "why-adam",
    timeline: "why-adam",
    contact: "why-adam",
    footer: "why-adam",
  },
  hero: {
    preparedFor: "Prepared for {name}",
    tagline: "A clear plan to sell smoothly and net the most the market will pay.",
    bookCall: "Book a call with Adam",
    seePlan: "See the plan",
    watchVideo: "Watch Adam's message",
    videoTitle: "A message from Adam",
    closeVideo: "Close video",
    facts: { beds: "bedrooms", baths: "baths", sqft: "sq ft", lot: "lot", built: "built" },
  },
  compared: {
    headline: "Your home, compared with {count} nearby homes",
    intro: "Buyers will compare yours with these {count}. Here's where it stands out.",
    lotRank: "Larger than {n} of the {total} nearby homes",
    lotStripCaption: "Each dot is a nearby lot. The ringed dot is yours.",
    lotStripSummary: "Nearby lots range from {min} to {max}. Yours is {value}.",
    builtLabel: "built",
    builtRank: "Newer than {n} of {total}.",
    bathsLabel: "baths",
    bathsRank: "More than {n} of {total}.",
    garageValue: "{n}-car",
    garageLabel: "attached garage",
    garageRank: "More than {n} of {total}.",
    settingTitle: "The setting",
    nearbyTitle: "Nearby",
    schoolsTitle: "Schools",
    updatesTitle: "Recent updates",
  },
  nearby: {
    headline: "Nearby Sales ({area})",
    intro: "Homes I've sold south of I-90, including several on streets near yours.",
    note: "These are my past sales, not price comparisons. For pricing, see Where your home fits.",
    statusSold: "Sold by Adam",
    statusListed: "Listed by Adam",
    beds: "{n} beds",
    baths: "{n} baths",
    prev: "Previous homes",
    next: "More homes",
    carouselLabel: "Homes Adam has sold nearby",
    swipeHint: "Swipe to see each home.",
    map: {
      title: "On the map",
      iframeTitle: "Map of homes Adam has sold near you",
      legend: { sold: "Sold by Adam", listed: "Listed by Adam", yourHome: "Your home" },
    },
  },
  market: {
    headline: "The {area} market today",
    gaugeTitle: "Months of inventory",
    gaugeValue: "{value} months of inventory",
    gaugeBands: { seller: "Seller's market", neutral: "Neutral", buyer: "Buyer's market" },
    gaugeRanges: { seller: "Under {n} months", neutral: "{a} to {b} months", buyer: "Over {n} months" },
    gaugeSummary: "{area} has {value} months of inventory: under {low} favors sellers, over {high} favors buyers.",
    gaugeMax: 9,
    yearAgo: "A year ago: {value}",
    chartTitle: "Month by month",
    tabs: [
      { key: "daysOnMarket", label: "Days on market", format: "days" },
      { key: "pricePerSqft", label: "Price per sq ft", format: "perSqft" },
      { key: "homesForSale", label: "Homes for sale", format: "count" },
    ],
    legendNow: "Last 12 months",
    legendPrior: "The 12 months before",
    chartSummary: "{metric} in {period}: {now}, compared with {prior} a year earlier.",
    seasonalBars: "Homes for sale",
    seasonalLine: "Price and competition",
    seasonalSummary: "Homes for sale bottom out in {low} and peak in {high}.",
  },
  fits: {
    headline: "Where your home fits",
    intro: "{count} comparable homes nearby, on one price line.",
    spectrum: { min: 1000000, max: 1750000, tickStep: 100000, tickStepMobile: 250000 },
    lanes: { sold: "Sold", forSale: "For sale", pending: "Pending" },
    yourHome: "Your home",
    bandRange: "{low} to {high}",
    legend: {
      sold: "Sold",
      active: "Active",
      firstLook: "First Look",
      pending: "Pending",
      size: "Larger dots are larger homes",
    },
    map: {
      title: "On the map",
      iframeTitle: "Map of your home and the {count} comparable homes",
      caption: "Tap any pin to see the price, status and listing firm.",
    },
    summary: "{sold} sold, {forSale} for sale and {pending} pending, priced from {min} to {max}.",
    summaryBand: "The gold band runs from {low} to {high}: {label}.",
    tooltip: {
      price: "Price",
      sqft: "Size",
      ppsf: "Price per sq ft",
      dom: "Days on market",
      firm: "Listing firm",
      close: "Close details",
    },
    dotLabel: "{address}, {status}, {price}",
    filters: [
      { key: "all", label: "All" },
      { key: "sold", label: "Sold" },
      { key: "forSale", label: "For sale" },
      { key: "pending", label: "Pending" },
    ],
    filterLabel: "Show homes",
    sortLabel: "Sort by",
    sortOptions: [
      { key: "price", label: "Price" },
      { key: "ppsf", label: "$/sq ft" },
      { key: "dom", label: "Days on market" },
      { key: "soldDate", label: "Sold date" },
    ],
    sortAsc: "Low to high",
    sortDesc: "High to low",
    carouselLabel: "Comparable homes",
    prev: "Previous homes",
    next: "More homes",
    tableToggleOpen: "Compare in a table",
    tableToggleClose: "Hide the table",
    columns: {
      address: "Address",
      status: "Status",
      price: "Price",
      originalPrice: "Original price",
      pctOfOriginal: "% of original",
      beds: "Beds",
      baths: "Baths",
      sqft: "Sq ft",
      lot: "Lot",
      yearBuilt: "Built",
      ppsf: "$/sq ft",
      dom: "Days on market",
      soldDate: "Sold date",
      firm: "Listing firm",
    },
    sortHint: "Sort by {column}",
    moreDetails: "More details for {address}",
    lessDetails: "Hide details for {address}",
    scrollHint: "Scroll sideways for more columns.",
    summaryRows: { sold: "Sold average", forSale: "For sale average", pending: "Pending average" },
    newConstruction: "{year} new construction",
    swipeHint: "Swipe to see each home.",
    cardCourtesy: "Listing courtesy of {firm}",
  },
  pricing: {
    headline: "Pricing right is the strategy",
    intro: "The first two weeks decide most sales.",
    courtesy: "Listing courtesy of {firm}",
    scatterTitle: "Sold homes: time on market and price kept",
    scatterX: "Days on market",
    scatterY: "% of original list price",
    scatterFirstWeek: "First week",
    firstWeekDays: 7,
    scatterNote: "Homes that sold in the first week kept the most of their price.",
    scatterSummary: "Sold in the first week: {fast} of original list on average. Sold later: {slow}.",
    scatterSkipped: "Not shown, days on market not reported: {list}.",
    ladderTitle: "What showings tell us",
    strategiesTitle: "Three ways to set the price",
    strategiesLabel: "Pricing strategy",
  },
  launch: { firstLookTitle: "First Look", blitzNavLabel: "Launch days" },
  ready: {
    headline: "Getting your home ready",
    intro: "From our first meeting to launch week, plus a short prep list that protects your price.",
    processTitle: "The listing process",
    checklistTitle: "Home preparation",
  },
  marketing: {
    headline: "How buyers will see your home",
    intro: "Most buyers meet a home on a screen first. I make that first look count.",
    tileIcons: [null, null, null, null, "Search", null, null, null],
    recentTitle: "Listings by Adam",
    prev: "Previous photos",
    next: "More photos",
  },
  communication: {
    headline: "You'll always know what's happening",
    intro: "A clear update every week, and an answer to every question.",
    reportTitle: "Your weekly report",
    stats: { showings: "Showings", openHouseVisitors: "Open house visitors", onlineViews: "Online views" },
    feedbackTitle: "Buyer feedback",
    phoneLabel: "A phone showing an example weekly report",
  },
  offers: {
    headline: "Offers, negotiated by an expert",
    intro: "Good marketing brings offers. Careful negotiation decides what you keep.",
    quoteBy: "{name}, {title}",
    anatomyTitle: "Anatomy of an offer",
    offerNames: ["Offer 1", "Offer 2"],
    reveal: "Tap to compare",
    hide: "Tap to hide",
    yes: "Yes",
    no: "No",
    strongTitle: "What makes an offer strong",
  },
  proceeds: {
    headline: "What you could walk away with",
    intro: "Move the price to see an estimate. Every cost is shown.",
    priceLabel: "Sale price",
    sliderLabel: "Sale price",
    resultLabel: "Estimated proceeds before your loan payoff",
    costsTitle: "Estimated costs",
    lines: {
      listingFirm: "Listing firm compensation",
      buyerBrokerage: "Buyer brokerage compensation",
      escrow: "Escrow fee",
      title: "Title policy",
      recording: "Recording fee",
      excise: "Excise tax",
      exciseDetail: "State {state} plus local {local}",
      prep: "Pre-listing prep",
      total: "Total estimated costs",
    },
    pctLabel: "{line}, percent of sale price",
    loanLabel: "Your remaining loan balance",
    loanHelp: "Optional. It stays on this screen and isn't saved or sent anywhere.",
    cashLabel: "Estimated cash at closing",
  },
  why: {
    headline: "Why sellers choose Adam",
    intro: "The track record behind the plan.",
    carouselLabel: "What sellers say",
    prev: "Previous review",
    next: "Next review",
    goTo: "Show review {n} of {total}",
    slideLabel: "{n} of {total}",
    videoTitle: "Hear it from clients",
    videoLabel: "Video reviews from clients",
    videoPrev: "Previous videos",
    videoNext: "More videos",
    videoPlay: "Play {name}'s video review",
    videoDialogLabel: "Video review from {name}",
    videoClose: "Close video",
    videoUnavailable: "This video isn't available right now.",
  },
  timeline: { headline: "From today to closing", intro: "{count} steps, and I'll walk you through each one." },
  cta: { book: "Book a call", call: "Call", text: "Text", email: "Email" },
  footer: {
    brokerLine: "{name}, {title}. {team}, brokered by {brokerage}.",
    license: "License",
    office: "Office",
    phone: "Phone",
    email: "Email",
    website: "Website",
    privateNote: "Prepared privately for {name}. Not for distribution.",
  },
  alt: {
    portraitNegotiation: "Adam Cothes standing in a kitchen",
    portraitThankYou: "Adam Cothes, seated, smiling",
    signature: "Adam Cothes's signature",
    logoTeam: "Adam Home Team",
    logoBrokerage: "eXp Realty",
    equalHousing: "Equal Housing Opportunity logo",
  },
  plan: {
    headline: "My marketing plan",
    intro: "Everything in this plan is paid by me.",
    includedLabel: "Included",
  },
  status: {
    notAvailable: "This page isn't available. Contact Adam Cothes at {phone} or {email}.",
    expired: "This plan has expired. Contact Adam Cothes for an updated one.",
    call: "Call Adam",
    email: "Email Adam",
    checklistTitle: "Draft checklist",
    checklistIntro: "Fix these before setting the proposal live.",
    checklistClear: "Nothing missing. This proposal is ready to go live.",
    checkReportTitle: "Proposal check",
    errorsLabel: "Must fix",
    warningsLabel: "Worth a look",
  },
  themePill: { label: "Theme", options: { signature: "Signature", sage: "Sage", mono: "Mono" } },
};
